import { iconCheckBlue, iconCircleSlashBlue } from "assets/icons";
import { Button } from "components/Button";
import { FilterProps } from "components/TableModified/TableModifiedFilters";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import Papa from "papaparse";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ActionButtonsWrapper } from "./ActionButtonsWrapper";

interface Property {
  name?: string;
  value?: string;
  action?: () => void;
}

type RegistrationProfile = {
  name: string;
  company: string;
  position: string;
  isSpeaker: boolean;
  displayPosition?: number;
};

export interface Registration {
  registrationId: string;
  status: "CREATED" | "APPROVED" | "CANCELLED" | "CUSTOMER_DELETED";
  profile: RegistrationProfile;
  insertDate: string;
  updateDate: string;
  customer: {
    name: string;
    email: string;
    phoneNumber: string;
    cpf: string;
  } | null;
  customerCpf?: string | null;
  importData?: {
    cpf?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  company: string;
  role: string;
  isSpeaker: boolean;
  event: {
    name: string;
  };
}

interface RegistrationRowProps {
  customerName: string;
  company: string;
  role: string;
  isSpeaker: string;
  displayPosition: number | string;
  status: string;
  actions: JSX.Element[];
}

export const registrationStatusMap: Record<string, string> = {
  CREATED: "Criado",
  APPROVED: "Aprovado",
  CANCELLED: "Cancelado",
  CUSTOMER_DELETED: "Excluído",
};

export const useRegistrationsTable = () => {
  const { id: eventId } = useParams<{ id: string }>();
  const initialTableFilters: FilterProps = {
    status: {
      label: "Status",
      value: "",
      options: [
        { value: "CREATED", label: "Criado" },
        { value: "APPROVED", label: "Aprovado" },
        { value: "CANCELLED", label: "Cancelado" },
        { value: "CUSTOMER_DELETED", label: "Excluído" },
      ],
    },
  };

  const [properties, setProperties] = useState<Property[]>();
  const [registrations, setRegistrations] = useState<Registration[]>();
  const [registrationRows, setRegistrationRows] = useState<
    RegistrationRowProps[]
  >([]);
  const [tableFilters, setTableFilters] =
    useState<FilterProps>(initialTableFilters);
  const [searchText, setSearchText] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [registrationsPerPage, setRegistrationsPerPage] = useState<number>(10);
  const [registrationsCount, setRegistrationsCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const searchTextFormRef = useRef<any>(null);
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();
  const { setToast } = useToast();

  /* HOOKS */
  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, [cancelRequest]);

  useEffect(() => {
    setPageNumber(1);
  }, [tableFilters, searchText, registrationsPerPage]);

  useEffect(() => {
    getRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableFilters, searchText, pageNumber, registrationsPerPage]);

  /* EVENT HANDLERS */
  const handleSearchTextFormSubmit = (event: any) => {
    event.preventDefault();
    const text = event.target.searchText.value;
    setSearchText(text);
  };

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const eventName = event.target.name;
    const eventValue = event.target.value;
    setTableFilters({
      ...tableFilters,
      [eventName]: {
        ...tableFilters[eventName],
        value: eventValue,
      },
    });
  };

  const handleFilterRemove = (filterName: string): void => {
    setTableFilters({
      ...tableFilters,
      [filterName]: {
        ...tableFilters[filterName],
        value: "",
      },
    });
  };

  const handleSearchTextRemove = (): void => {
    searchTextFormRef.current.reset();
    setSearchText("");
  };

  const handlePageSelected = (page: number): void => {
    setPageNumber(page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const eventValue = event.target.value;
    setRegistrationsPerPage(parseInt(eventValue));
  };

  const handleUpdateRegistrationStatus = async (
    registrationId: string,
    status: "approved" | "rejected"
  ) => {
    const mutation = `
      mutation BackofficeUpdateRegistration($registrationId: ID!, $input: BackOfficeRegistrationUpdateInput!) {
        backofficeUpdateRegistration(registrationId: $registrationId, input: $input) {
          registrationId
          status
          approvedBy {
            name
            userId
          }
        }
      }
    `;

    const variables = {
      registrationId,
      input: {
        status: status === "approved" ? "APPROVED" : "REJECTED",
      },
    };

    try {
      await connectGraphQLApi({
        query: mutation,
        variables,
        errorMessage: "Erro ao atualizar status da inscrição",
        useNewApi: true,
      });
      getRegistrations();
      setToast({
        type: "success",
        message:
          status === "approved"
            ? "Inscrição aprovada com sucesso!"
            : "Inscrição reprovada com sucesso!",
        isActive: true,
        timeToRemoveToast: 3000,
      });
    } catch (error) {
      setToast({
        type: "error",
        message: "Erro ao atualizar status da inscrição.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
      console.error("[handleUpdateRegistrationStatus]", error);
    }
  };

  /* EXPORT HANDLER */
  const handleExportCsv = async () => {
    setExporting(true);

    const exportQuery = `
      query BackofficeRegistrationsEventExport($eventId: ID!) {
        backofficeRegistrationsEvent(eventId: $eventId) {
          registrationId
          status
          insertDate
          customerCpf
          customer {
            name
            email
            phoneNumber
            cpf
          }
          importData {
            cpf
            email
            phone
          }
          profile {
            name
            company
            position
            isSpeaker
            displayPosition
          }
        }
      }
    `;

    try {
      const response = await connectGraphQLApi({
        query: exportQuery,
        variables: { eventId },
        errorMessage: "Erro ao exportar inscrições",
        useNewApi: true,
      });

      const registrations: Registration[] =
        response?.data?.backofficeRegistrationsEvent || [];

      if (registrations.length === 0) {
        setToast({
          type: "warning",
          message: "Não há inscrições para exportar.",
          isActive: true,
          timeToRemoveToast: 4000,
        });
        return;
      }

      const rows = registrations.map((registration) => ({
        Nome: registration?.customer?.name ?? registration?.profile?.name ?? "",
        "E-mail":
          registration?.customer?.email ??
          registration?.importData?.email ??
          "",
        Telefone:
          registration?.customer?.phoneNumber ??
          registration?.importData?.phone ??
          "",
        CPF:
          registration?.customer?.cpf ??
          registration?.customerCpf ??
          registration?.importData?.cpf ??
          "",
        Empresa: registration?.profile?.company ?? "",
        Cargo: registration?.profile?.position ?? "",
        "É Palestrante": registration?.profile?.isSpeaker ? "Sim" : "Não",
        Ordem: registration?.profile?.displayPosition ?? "",
        Status: registrationStatusMap[registration.status] ?? registration.status,
        "Data de Inscrição": registration?.insertDate
          ? new Date(registration.insertDate).toLocaleString("pt-BR")
          : "",
      }));

      const csv = Papa.unparse(rows);
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `inscricoes-evento-${eventId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setToast({
        type: "success",
        message: "Inscrições exportadas com sucesso!",
        isActive: true,
        timeToRemoveToast: 4000,
      });
    } catch (error) {
      setToast({
        type: "error",
        message: "Erro ao exportar inscrições.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
      console.error("[handleExportCsv]", error);
    } finally {
      setExporting(false);
    }
  };

  /* API CALLS */
  const getRegistrations = async () => {
    setLoading(true);
    cancelRequest();

    // eventRegistrationsCount(eventId: $eventId) TODO: Implementar query/resolver para contar inscrições

    const query = `
      query BackofficeRegistrationsEvent($eventId: ID!, $filter: BackofficeRegistrationFilterInput, $pagination: PaginationInput) {
        backofficeRegistrationsEvent(eventId: $eventId, filter: $filter, pagination: $pagination) {
          registrationId
          status
          insertDate
          updateDate
          customerId
          customer {
            name
            email
            phoneNumber
            cpf
          }
          profile {
            name
            company
            position
            isSpeaker
            displayPosition
            bio
            linkedinUrl
            twitterUrl
            photo {
              signedFileUrl
            }
          }
        }
      }
    `;

    const countQuery = `
      query BackofficeRegistrationsEventCount($eventId: ID!, $filter: BackofficeRegistrationFilterInput) {
        backofficeRegistrationsCount(eventId: $eventId, filter: $filter){
          total
          approved
          pending
          redeemed
        }
      }
    `;

    const filter = {
      status: tableFilters.status?.value || undefined,
      searchText: searchText || undefined,
    };

    const variables = {
      eventId,
      filter,
      pagination: {
        offset: (pageNumber - 1) * registrationsPerPage,
        limit: registrationsPerPage,
      },
    };

    try {
      const response = await connectGraphQLApi({
        query,
        variables,
        errorMessage: "Falha ao listar inscrições",
        useNewApi: true,
      });

      const countResponse = await connectGraphQLApi({
        query: countQuery,
        variables: {
          eventId,
          filter,
        },
        errorMessage: "Falha ao contar inscrições",
        useNewApi: true,
      });

      setError(false);
      const responseData = (response as any).data;
      const registrations: Registration[] =
        responseData.backofficeRegistrationsEvent || [];
      const registrationsCount =
        countResponse?.data?.backofficeRegistrationsCount?.total ?? 0;
      const approvedCount =
        countResponse?.data?.backofficeRegistrationsCount?.approved ?? 0;
      const pendingCount =
        countResponse?.data?.backofficeRegistrationsCount?.pending ?? 0;
      const redeemedCount =
        countResponse?.data?.backofficeRegistrationsCount?.redeemed ?? 0;

      // Create properties based on the registrations data
      const properties: Property[] = [
        { name: "Total de Inscrições", value: registrationsCount.toString() },
        { name: "Aprovados", value: approvedCount.toString() },
        { name: "Pendentes", value: pendingCount.toString() },
        { name: "Resgatados", value: redeemedCount.toString() },
      ];

      const registrationRows = registrations.map(
        (registration: Registration) => {
          let actions: JSX.Element[] = [];
          if (registration.status === "CREATED") {
            actions = [
              <ActionButtonsWrapper>
                <Button
                  key="approve"
                  label="Aprovar"
                  size="small"
                  typeBtn="outline"
                  icon={iconCheckBlue}
                  iconSide="right"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    handleUpdateRegistrationStatus(
                      registration.registrationId,
                      "approved"
                    );
                  }}
                />
                <Button
                  key="reject"
                  label="Reprovar"
                  size="small"
                  typeBtn="default"
                  icon={iconCircleSlashBlue}
                  iconSide="right"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    handleUpdateRegistrationStatus(
                      registration.registrationId,
                      "rejected"
                    );
                  }}
                />
              </ActionButtonsWrapper>,
            ];
          }
          return {
            customerName:
              registration?.customer?.name ?? registration?.profile?.name,
            company: registration?.profile?.company ?? "Não informado",
            role: registration?.profile?.position ?? "Não informado",
            isSpeaker: registration?.profile?.isSpeaker ? "Sim" : "Não",
            displayPosition:
              registration?.profile?.displayPosition || "Não informado",
            status: registrationStatusMap[registration.status],
            actions,
          };
        }
      );

      setProperties(properties);
      setRegistrations(registrations);
      setRegistrationsCount(registrationsCount);
      setRegistrationRows(registrationRows);
      setLoading(false);
    } catch (error: any) {
      console.error("Error details:", error);
      if (error?.errorType === "connection") {
        setError(true);
        setLoading(false);
      }
    }
  };

  return {
    loading,
    error,
    properties,
    registrationRows,
    registrationsCount,
    searchText,
    tableFilters,
    searchTextFormRef,
    pageNumber,
    registrationsPerPage,
    handleSearchTextFormSubmit,
    handleFilterChange,
    handleFilterRemove,
    handleSearchTextRemove,
    handlePageSelected,
    handleRowsPerPageChange,
    registrations,
    handleUpdateRegistrationStatus,
    getRegistrations,
    handleExportCsv,
    exporting,
  };
};
